import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpDown,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Wrench,
  X,
} from 'lucide-react';

import { documentsAPI, maintenanceAPI, printingAPI, trucksAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { truckOption } from '../lib/selectOptions';
import {
  BREAKDOWN_STATUS_LABELS,
  DOC_COMPONENT_LABELS,
  DOC_TYPE_LABELS,
  IMMO_STATUS_LABELS,
  PRIORITY_LABELS,
  READING_TYPE_LABELS,
  REASON_CODE_LABELS,
  SEVERITY_LABELS,
  TRIGGER_LABELS,
  WORK_STATUS_LABELS,
  WORK_TYPE_LABELS,
} from '../lib/maintenanceLabels';
import { accorde, formatDate, formatDateHeure, formatMontant } from '../lib/formats';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { inboundRentalsAPI } from '../lib/inboundRentalsApi';
import { messageErreur } from '../lib/erreurs';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { FieldError } from '../components/ui/field-error';
import { PageHero } from '../components/dashboard/PageHero';
import { BlocagesParCamion } from '../components/maintenance/BlocagesParCamion';
import { PrintMenu } from '../components/business/PrintMenu';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { RegistryToolbar } from '../components/ui/page-layouts';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from '../components/ui/sonner';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import MaintenanceSpendHeader, { MAINTENANCE_WINDOWS as FENETRES, maintenanceSpendSeries } from '../components/maintenance/MaintenanceSpendHeader';
import { SectionCard } from '../components/dashboard';
import { Citerne } from '../components/ui/icons/domain-icons';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

/**
 * Maintenance — un poste de travail, sept vues sœurs.
 *
 * L'écran répond à une seule question: qu'est-ce qui empêche un camion de
 * partir aujourd'hui, et combien cela coûte. D'où l'ordre: le bandeau
 * d'indicateurs en haut sert de point d'entrée (chaque tuile pose le filtre
 * qui la justifie), les onglets descendent ensuite du parc vers la pièce
 * détachée.
 *
 * Deux règles tiennent tout le fichier:
 *  - aucun indicateur n'est recalculé ici; les listes sont paginées côté
 *    serveur et une moyenne prise sur une page est un mensonge;
 *  - la couleur marque un état, jamais une position. Les `tone` de `Readout`
 *    et les variantes de `Badge` sont la seule table de couleurs.
 */

/* ------------------------------------------------------------------ */
/* Vocabulaire du domaine                                              */
/* ------------------------------------------------------------------ */

// Les natures de papiers (DOC_TYPE_LABELS) et les éléments du camion
// (DOC_COMPONENT_LABELS) viennent de lib/maintenanceLabels, partagés avec la
// fiche camion.

// « Non précisé » est stocké NULL côté serveur; un menu ne sait pas porter une
// valeur vide, d'où ce jeton.
const NO_COMPONENT = '__none__';

// Type de pièce archivée selon la nature du papier; le reste est « OTHER ».
const SCAN_DOC_TYPE = {
  insurance: 'INSURANCE',
  technical_inspection: 'TECHNICAL_INSPECTION',
  registration: 'VEHICLE_REGISTRATION',
};

// Les libellés des statuts, types et motifs viennent de lib/maintenanceLabels
// (qui lit lib/libelles.js): une seule table de mots pour toute l'application.

/* ------------------------------------------------------------------ */
/* Onglets, routes et filtres serveur                                  */
/* ------------------------------------------------------------------ */

/**
 * Chaque onglet garde son URL: la barre latérale et les liens existants
 * pointent déjà sur ces chemins, et un onglet purement local rendrait le
 * partage d'un écran impossible.
 *
 * `statusFilter` décrit le second filtre serveur de l'onglet — le paramètre
 * n'a pas le même nom partout (`status`, `doc_type`, `reading_type`), mais la
 * barre d'outils n'a pas à le savoir.
 */
const TABS = [
  { key: 'overview', label: 'Vue d’ensemble', path: '/maintenance' },
  {
    key: 'readings',
    label: 'Compteurs',
    path: '/maintenance/readings',
    statusFilter: { param: 'reading_type', label: 'Type', options: READING_TYPE_LABELS },
  },
  { key: 'plans', label: 'Préventif', path: '/maintenance/plans' },
  {
    key: 'workOrders',
    label: 'Ordres de travail',
    path: '/maintenance/work-orders',
    statusFilter: { param: 'status', label: 'Statut', options: WORK_STATUS_LABELS },
  },
  {
    key: 'breakdowns',
    label: 'Pannes',
    path: '/maintenance/breakdowns',
    statusFilter: { param: 'status', label: 'Statut', options: BREAKDOWN_STATUS_LABELS },
  },
  {
    key: 'documents',
    label: 'Échéances',
    path: '/maintenance/documents',
    statusFilter: { param: 'doc_type', label: 'Nature', options: DOC_TYPE_LABELS },
  },
  {
    key: 'immobilizations',
    label: 'Immobilisations',
    path: '/maintenance/immobilizations',
    statusFilter: { param: 'status', label: 'Statut', options: IMMO_STATUS_LABELS },
  },
];

const TAB_BY_PATH = Object.fromEntries(TABS.map((tab) => [tab.path, tab]));
const TAB_BY_KEY = Object.fromEntries(TABS.map((tab) => [tab.key, tab]));

const PERIODS = {
  all: { label: 'Depuis toujours', days: null },
  7: { label: '7 derniers jours', days: 7 },
  30: { label: '30 derniers jours', days: 30 },
  90: { label: '90 derniers jours', days: 90 },
  365: { label: '12 derniers mois', days: 365 },
};

/**
 * Fenêtre d'observation du panneau de parc.
 *
 * Elle est distincte de `PERIODS`, qui filtre une liste: le panneau observe
 * une durée (« sur 30 jours »), la liste coupe un historique (« les 30
 * derniers jours »). Deux phrases, deux réglages, et les mélanger ferait
 * bouger les indicateurs quand on filtre un tableau.
 */
const WRITE_ROLES = ['SUPER_ADMIN', 'ADMIN_SOCIETE', 'LOGISTIQUE', 'MAINTENANCE'];

/* ------------------------------------------------------------------ */
/* Formatage                                                            */
/* ------------------------------------------------------------------ */

const num = (value) => Number(value || 0);
const money = (value) => formatMontant(num(value));
const dateLabel = (value) => formatDate(value);
const dateTime = (value) => formatDateHeure(value);
const counter = (value, type) =>
  `${num(value).toLocaleString('fr-FR')} ${type === 'hours' ? 'h' : 'km'}`;

/**
 * Une mesure absente s'écrit « — », jamais « 0 ».
 *
 * « 0 XOF/km » affirme que rouler ne coûte rien, et « 0 panne » qu'aucune
 * n'est survenue: deux phrases fausses quand la vérité est qu'aucun relevé
 * n'a été saisi ou que le calcul n'a pas abouti. Le serveur distingue déjà les
 * deux — il renvoie `null` pour l'inconnu et un nombre pour le mesuré —, et
 * cet écran doit tenir la distinction, parce qu'un zéro se commente en réunion
 * alors qu'un « — » se va chercher.
 */
const mesure = (value, decimales = 0) =>
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('fr-FR', { maximumFractionDigits: decimales });

/** Convertit à la validation seulement: « 0. » doit rester saisissable. */
const toNumber = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const parsed = Number(String(raw).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/* ------------------------------------------------------------------ */
/* Descriptions de formulaires                                          */
/* ------------------------------------------------------------------ */

/**
 * Les formulaires sont décrits, pas écrits.
 *
 * Six entités partageaient six variantes du même bloc de champs, chacune avec
 * ses oublis: ici un `<Label>` manquant, là une erreur jamais affichée, là un
 * booléen déguisé en liste « oui / non ». Décrire les champs garantit que
 * chacun a son libellé, sa validation et son état d'envoi — l'accessibilité
 * cesse d'être une relecture.
 */

const selectOptions = (labels) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const FORMS = {
  reading: {
    title: 'Nouveau relevé compteur',
    description:
      'Un relevé ne peut pas être inférieur au précédent : un compteur qui recule fait mentir tous les plans préventifs.',
    submitLabel: 'Enregistrer le relevé',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true },
      {
        name: 'reading_type',
        label: 'Nature du compteur',
        type: 'select',
        options: selectOptions(READING_TYPE_LABELS),
      },
      {
        name: 'value',
        label: 'Valeur relevée',
        type: 'number',
        required: true,
        hint: 'Le chiffre lu au tableau de bord.',
      },
      { name: 'note', label: 'Note', type: 'text', full: true },
    ],
    initial: { truck_id: '', reading_type: 'km', value: '', note: '' },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      reading_type: form.reading_type,
      value: toNumber(form.value),
      note: form.note || null,
    }),
    validate: (form) => {
      const errors = {};
      if (!form.truck_id) errors.truck_id = 'Choisissez le camion relevé.';
      if (toNumber(form.value) === null) errors.value = 'Saisissez la valeur du compteur.';
      else if (toNumber(form.value) < 0) errors.value = 'Un compteur ne peut pas être négatif.';
      return errors;
    },
  },

  plan: {
    title: 'Plan préventif',
    description:
      'Le seuil d’alerte est ce qui laisse le temps de réserver un créneau atelier avant l’échéance.',
    submitLabel: 'Enregistrer le plan',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true, createOnly: true },
      { name: 'name', label: 'Intitulé', type: 'text', required: true, placeholder: 'Vidange moteur' },
      {
        name: 'trigger_type',
        label: 'Déclencheur',
        type: 'select',
        options: selectOptions(TRIGGER_LABELS),
        createOnly: true,
      },
      {
        name: 'last_service_km',
        label: 'Dernier entretien (km)',
        type: 'number',
        createOnly: true,
      },
      {
        name: 'interval_km',
        label: 'Intervalle (km)',
        type: 'number',
        when: (form) => form.trigger_type !== 'date',
      },
      {
        name: 'alert_threshold_km',
        label: 'Alerte avant (km)',
        type: 'number',
        when: (form) => form.trigger_type !== 'date',
      },
      {
        name: 'interval_days',
        label: 'Intervalle (jours)',
        type: 'number',
        when: (form) => form.trigger_type !== 'mileage',
      },
      {
        name: 'alert_threshold_days',
        label: 'Alerte avant (jours)',
        type: 'number',
        when: (form) => form.trigger_type !== 'mileage',
      },
      {
        name: 'estimated_cost',
        label: 'Budget prévu (FCFA)',
        type: 'number',
        hint: 'Sert d’étalon à la clôture de l’ordre : l’écart au réel est affiché.',
        createOnly: true,
      },
      { name: 'task_description', label: 'Tâche à effectuer', type: 'textarea', full: true },
      {
        name: 'immobilizes',
        label: 'Immobilise le camion à l’échéance',
        type: 'switch',
        full: true,
      },
      { name: 'is_active', label: 'Plan actif', type: 'switch', full: true, editOnly: true },
    ],
    initial: {
      truck_id: '',
      name: '',
      trigger_type: 'mileage',
      last_service_km: '',
      interval_km: '',
      alert_threshold_km: '',
      interval_days: '',
      alert_threshold_days: '',
      estimated_cost: '',
      task_description: '',
      immobilizes: false,
      is_active: true,
    },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      name: form.name,
      trigger_type: form.trigger_type,
      task_description: form.task_description || null,
      interval_km: toNumber(form.interval_km),
      interval_days: toNumber(form.interval_days),
      alert_threshold_km: toNumber(form.alert_threshold_km),
      alert_threshold_days: toNumber(form.alert_threshold_days),
      last_service_km: toNumber(form.last_service_km),
      estimated_cost: toNumber(form.estimated_cost),
      immobilizes: Boolean(form.immobilizes),
    }),
    toPatch: (form) => ({
      name: form.name,
      task_description: form.task_description || null,
      interval_km: toNumber(form.interval_km),
      interval_days: toNumber(form.interval_days),
      alert_threshold_km: toNumber(form.alert_threshold_km),
      alert_threshold_days: toNumber(form.alert_threshold_days),
      immobilizes: Boolean(form.immobilizes),
      is_active: Boolean(form.is_active),
    }),
    validate: (form, { editing }) => {
      const errors = {};
      if (!editing && !form.truck_id) errors.truck_id = 'Choisissez le camion concerné.';
      if (!form.name.trim()) errors.name = 'Nommez l’entretien (ex. Vidange moteur).';
      if (form.trigger_type !== 'date' && toNumber(form.interval_km) === null) {
        errors.interval_km = 'Un plan au kilométrage a besoin de son intervalle.';
      }
      if (form.trigger_type !== 'mileage' && toNumber(form.interval_days) === null) {
        errors.interval_days = 'Un plan au calendrier a besoin de son intervalle.';
      }
      return errors;
    },
  },

  workOrder: {
    title: 'Ordre de travail',
    description:
      '« Immobilise le camion » crée l’immobilisation qui refusera toute affectation à un trajet.',
    submitLabel: 'Ouvrir l’ordre',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true, createOnly: true },
      { name: 'title', label: 'Intitulé de l’intervention', type: 'text', required: true },
      {
        name: 'work_type',
        label: 'Nature',
        type: 'select',
        options: selectOptions(WORK_TYPE_LABELS),
        createOnly: true,
      },
      {
        name: 'priority',
        label: 'Priorité',
        type: 'select',
        options: selectOptions(PRIORITY_LABELS),
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: selectOptions(WORK_STATUS_LABELS),
        editOnly: true,
      },
      { name: 'workshop_name', label: 'Atelier ou prestataire', type: 'text' },
      { name: 'workshop_contact', label: 'Contact atelier', type: 'text' },
      { name: 'scheduled_date', label: 'Date prévue', type: 'date' },
      { name: 'labor_cost', label: 'Main d’œuvre estimée (FCFA)', type: 'number' },
      { name: 'description', label: 'Travail à réaliser', type: 'textarea', full: true },
      // Visible seulement si le camion est loué chez un transporteur (voir RecordDialog).
      { name: 'cost_bearer', label: 'Qui paie ?', type: 'bearer', category: () => 'ENTRETIEN_REPARATION' },
      {
        name: 'immobilizes',
        label: 'Immobilise le camion',
        type: 'switch',
        full: true,
        createOnly: true,
      },
    ],
    initial: {
      truck_id: '',
      title: '',
      work_type: 'corrective',
      priority: 'normal',
      status: 'planned',
      workshop_name: '',
      workshop_contact: '',
      scheduled_date: '',
      labor_cost: '',
      description: '',
      cost_bearer: '',
      immobilizes: false,
    },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      title: form.title,
      description: form.description || null,
      work_type: form.work_type,
      priority: form.priority,
      workshop_name: form.workshop_name || null,
      workshop_contact: form.workshop_contact || null,
      scheduled_date: form.scheduled_date || null,
      labor_cost: toNumber(form.labor_cost),
      cost_bearer: form.cost_bearer || null,
      immobilizes: Boolean(form.immobilizes),
    }),
    toPatch: (form) => ({
      title: form.title,
      description: form.description || null,
      status: form.status,
      priority: form.priority,
      workshop_name: form.workshop_name || null,
      workshop_contact: form.workshop_contact || null,
      scheduled_date: form.scheduled_date || null,
      labor_cost: toNumber(form.labor_cost),
      cost_bearer: form.cost_bearer || null,
    }),
    validate: (form, { editing }) => {
      const errors = {};
      if (!editing && !form.truck_id) errors.truck_id = 'Choisissez le camion à intervenir.';
      if (!form.title.trim()) errors.title = 'Décrivez l’intervention en une ligne.';
      return errors;
    },
  },

  breakdown: {
    title: 'Panne signalée',
    description:
      'Une panne critique immobilise le camion immédiatement, sans attendre de validation.',
    submitLabel: 'Signaler la panne',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true, createOnly: true },
      {
        name: 'severity',
        label: 'Gravité',
        type: 'select',
        options: selectOptions(SEVERITY_LABELS),
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: selectOptions(BREAKDOWN_STATUS_LABELS),
        editOnly: true,
      },
      { name: 'category', label: 'Organe', type: 'text', placeholder: 'Moteur, freins…' },
      // Lieu, compteur et constat sont le témoignage du terrain: le serveur ne
      // les rouvre pas à la modification, et un champ qu'on peut éditer sans
      // qu'il soit enregistré est pire que pas de champ du tout.
      { name: 'location_label', label: 'Lieu constaté', type: 'text', createOnly: true },
      { name: 'odometer_km', label: 'Compteur au constat (km)', type: 'number', createOnly: true },
      {
        name: 'description',
        label: 'Ce qui a été constaté',
        type: 'textarea',
        required: true,
        full: true,
        createOnly: true,
      },
      {
        name: 'resolution_note',
        label: 'Note de traitement',
        type: 'textarea',
        full: true,
        editOnly: true,
      },
    ],
    initial: {
      truck_id: '',
      severity: 'minor',
      status: 'open',
      category: '',
      location_label: '',
      odometer_km: '',
      description: '',
      resolution_note: '',
    },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      severity: form.severity,
      category: form.category || null,
      location_label: form.location_label || null,
      odometer_km: toNumber(form.odometer_km),
      description: form.description,
    }),
    toPatch: (form) => ({
      status: form.status,
      severity: form.severity,
      category: form.category || null,
      resolution_note: form.resolution_note || null,
    }),
    validate: (form, { editing }) => {
      const errors = {};
      if (!editing && !form.truck_id) errors.truck_id = 'Choisissez le camion en panne.';
      if (!editing && !form.description.trim()) {
        errors.description = 'Décrivez ce qui a été constaté : l’atelier n’a que cela.';
      }
      return errors;
    },
  },

  document: {
    title: 'Échéance documentaire',
    description:
      '« Bloque l’affectation » fait de l’expiration un refus effectif, pas seulement une alerte.',
    submitLabel: 'Enregistrer l’échéance',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true, createOnly: true },
      {
        name: 'doc_type',
        label: 'Nature du document',
        type: 'select',
        options: selectOptions(DOC_TYPE_LABELS),
        createOnly: true,
      },
      {
        name: 'component',
        label: 'Élément',
        type: 'select',
        options: [
          { value: NO_COMPONENT, label: 'Non précisé' },
          ...selectOptions(DOC_COMPONENT_LABELS),
        ],
        createOnly: true,
        hint: 'Tracteur, citerne ou ensemble : une même nature peut avoir un papier par élément.',
      },
      { name: 'reference', label: 'Référence ou n° de police', type: 'text' },
      { name: 'issuer', label: 'Émetteur', type: 'text' },
      { name: 'issued_date', label: 'Date d’émission', type: 'date' },
      { name: 'expiry_date', label: 'Expire le', type: 'date', required: true },
      {
        name: 'alert_days_before',
        label: 'Alerte (jours avant)',
        type: 'number',
        hint: 'Le délai dont l’atelier a besoin pour renouveler.',
      },
      { name: 'cost', label: 'Coût du renouvellement (FCFA)', type: 'number' },
      // Visible seulement si le camion est loué chez un transporteur (voir RecordDialog).
      {
        name: 'cost_bearer',
        label: 'Qui paie ?',
        type: 'bearer',
        category: (form) => (form.doc_type === 'insurance' ? 'ASSURANCE' : 'PIECES_ADMIN'),
      },
      { name: 'notes', label: 'Note', type: 'textarea', full: true },
      {
        name: 'scan',
        label: 'Scan du papier',
        type: 'file',
        full: true,
        hint: 'Photo ou PDF, rattaché à la fiche du camion.',
      },
      {
        name: 'blocks_assignment',
        label: 'Bloque l’affectation à l’expiration',
        type: 'switch',
        full: true,
      },
    ],
    // Dépose le scan sur le camion avant d'enregistrer l'échéance; l'identifiant
    // de la pièce archivée part dans `document_id`.
    upload: async (form) => {
      const archived = await documentsAPI.upload(
        form.scan,
        SCAN_DOC_TYPE[form.doc_type] || 'OTHER',
        { entity_type: 'truck', entity_id: form.truck_id },
      );
      return archived?.id ? { document_id: archived.id } : {};
    },
    initial: {
      truck_id: '',
      doc_type: 'insurance',
      component: NO_COMPONENT,
      reference: '',
      issuer: '',
      issued_date: '',
      scan: null,
      expiry_date: '',
      alert_days_before: '30',
      cost: '',
      cost_bearer: '',
      notes: '',
      blocks_assignment: false,
    },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      doc_type: form.doc_type,
      component: form.component && form.component !== NO_COMPONENT ? form.component : null,
      reference: form.reference || null,
      issuer: form.issuer || null,
      issued_date: form.issued_date || null,
      expiry_date: form.expiry_date,
      alert_days_before: toNumber(form.alert_days_before) ?? 30,
      cost: toNumber(form.cost),
      cost_bearer: form.cost_bearer || null,
      notes: form.notes || null,
      blocks_assignment: Boolean(form.blocks_assignment),
    }),
    // Le PATCH du serveur attend la pièce entière (camion et nature compris),
    // pas seulement les champs modifiés.
    toPatch: (form) => FORMS.document.toPayload(form),
    validate: (form, { editing }) => {
      const errors = {};
      if (!editing && !form.truck_id) errors.truck_id = 'Choisissez le camion concerné.';
      if (!form.expiry_date) errors.expiry_date = 'Sans date d’expiration, rien n’alertera.';
      if (form.issued_date && form.expiry_date && form.issued_date > form.expiry_date) {
        errors.issued_date = 'L’émission ne peut pas suivre l’expiration.';
      }
      return errors;
    },
  },

  immobilization: {
    title: 'Immobiliser un camion',
    description:
      'Le camion sera refusé à l’affectation tant que l’immobilisation n’est pas levée.',
    submitLabel: 'Immobiliser',
    fields: [
      { name: 'truck_id', label: 'Camion', type: 'truck', required: true },
      {
        name: 'reason_code',
        label: 'Motif',
        type: 'select',
        options: selectOptions(REASON_CODE_LABELS),
      },
      { name: 'expected_end_at', label: 'Remise en route prévue', type: 'date' },
      {
        name: 'reason',
        label: 'Ce qui immobilise le camion',
        type: 'textarea',
        required: true,
        full: true,
        hint: 'Lu par la répartition au moment du refus : soyez précis.',
      },
    ],
    initial: { truck_id: '', reason_code: 'manual', expected_end_at: '', reason: '' },
    toPayload: (form) => ({
      truck_id: form.truck_id,
      reason: form.reason,
      reason_code: form.reason_code,
      expected_end_at: form.expected_end_at || null,
    }),
    validate: (form) => {
      const errors = {};
      if (!form.truck_id) errors.truck_id = 'Choisissez le camion à immobiliser.';
      if (!form.reason.trim()) errors.reason = 'Écrivez le motif : il sera lu par la répartition.';
      return errors;
    },
  },
};

/* ------------------------------------------------------------------ */
/* Écran                                                               */
/* ------------------------------------------------------------------ */

export default function MaintenancePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const canWrite = WRITE_ROLES.includes(user?.role);
  const canReportBreakdown = canWrite || user?.role === 'CHAUFFEUR';

  const activeTab = TAB_BY_PATH[location.pathname] || TABS[0];
  const tabKey = activeTab.key;

  /* --- filtres ---------------------------------------------------- */
  const [truckId, setTruckId] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [period, setPeriod] = useState('all');
  const [search, setSearch] = useState('');
  // La frappe reste immédiate à l'écran ; seule la requête attend.
  const debouncedSearch = useDebouncedValue(search);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  /* --- données ---------------------------------------------------- */
  const [trucks, setTrucks] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [kpiPeriod, setKpiPeriod] = useState(30);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiError, setKpiError] = useState(false);
  const kpiRequest = useRef(0);

  const [rawList, setList] = useState({ items: [], total: 0, pages: 1, tab: null });
  // Les lignes de l'onglet quitté ne doivent jamais être lues avec les
  // colonnes du nouvel onglet: on ne garde que ce qui vient de l'onglet courant.
  const list = useMemo(
    () => (rawList.tab === tabKey ? rawList : { items: [], total: 0, pages: 1, tab: tabKey }),
    [rawList, tabKey],
  );
  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  // Changer d'onglet ou de filtre remet en page 1: rester en page 4 d'une
  // liste qui n'en compte plus qu'une affiche un vide qui n'existe pas.
  const pagination = usePaginationServeur(list.total, {
    reinitialiser: `${tabKey}|${truckId}|${statusValue}|${period}`,
  });
  const { page, limit: pageSize, allerA: allerAPage } = pagination;
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  // Le squelette n'a droit de cité qu'au tout premier chargement d'un onglet :
  // remplacer la page à chaque rafraîchissement ferait disparaître la barre de
  // recherche sous les doigts.
  const loadedTabs = useRef(new Set());
  const firstLoad = !loadedTabs.current.has(tabKey);

  const [overviewData, setOverviewData] = useState({
    immobilizations: [],
    plans: [],
    workOrders: [],
    workOrdersTruncated: false,
  });

  /* --- dialogues -------------------------------------------------- */
  const [dialog, setDialog] = useState(null); // { kind, record, prefill }
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [partsTarget, setPartsTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  /* ---------------------------------------------------------------- */
  /* Chargements                                                       */
  /* ---------------------------------------------------------------- */

  const serverParams = useMemo(() => {
    const params = { page, page_size: pageSize };
    if (truckId) params.truck_id = truckId;
    if (statusValue && activeTab.statusFilter) {
      params[activeTab.statusFilter.param] = statusValue;
    }
    const days = PERIODS[period]?.days;
    if (days) params.date_from = isoDaysAgo(days);
    return params;
  }, [page, pageSize, truckId, statusValue, period, activeTab]);

  const loadTrucks = useCallback(async () => {
    try {
      // Toutes les pages: le sélecteur de camion cherche dans ce qu'il reçoit.
      const result = await fetchAllPages((params) => trucksAPI.getAllPaged(params));
      setTrucks(result.items);
    } catch {
      // Le parc sert d'annuaire de libellés : son absence dégrade l'écran,
      // elle ne le casse pas. Un toast de plus noierait l'erreur utile.
    }
  }, []);

  const loadKpis = useCallback(async ({ silencieux = false } = {}) => {
    const request = ++kpiRequest.current;
    if (!silencieux) setKpiLoading(true);
    setKpiError(false);
    try {
      const result = await maintenanceAPI.getKpis({ period_days: kpiPeriod });
      if (request === kpiRequest.current) setKpis(result);
    } catch {
      if (request === kpiRequest.current) { setKpis(null); setKpiError(true); }
    } finally {
      if (request === kpiRequest.current) setKpiLoading(false);
    }
  }, [kpiPeriod]);

  // Vue d'ensemble et listes partagent l'écran: seule la dernière lecture écrit.
  const nouvelleDemande = useDerniereDemande();
  const loadOverview = useCallback(async () => {
    const perimee = nouvelleDemande();
    setListLoading(true);
    setListError(null);
    try {
      const [immobilizations, plans, orders] = await Promise.all([
        maintenanceAPI.getImmobilizationsPage({ status: 'active', page_size: 100 }),
        maintenanceAPI.getPlans(truckId ? { truck_id: truckId } : {}),
        maintenanceAPI.getWorkOrdersPage({ page_size: 200 }),
      ]);
      if (perimee()) return;
      setOverviewData({
        immobilizations: immobilizations.items,
        plans: Array.isArray(plans) ? plans : [],
        workOrders: orders.items,
        workOrdersTruncated: orders.total > orders.items.length,
      });
    } catch (error) {
      if (perimee()) return;
      setListError(error);
    } finally {
      if (!perimee()) {
        setListLoading(false);
        loadedTabs.current.add('overview');
      }
    }
  }, [truckId, nouvelleDemande]);

  const loadList = useCallback(async ({ silencieux = false } = {}) => {
    if (tabKey === 'overview') return loadOverview();
    const perimee = nouvelleDemande();
    if (!silencieux) setListLoading(true);
    setListError(null);
    try {
      if (tabKey === 'plans') {
        // Seule liste que le serveur ne pagine pas : elle porte l'état
        // d'échéance calculé, et le tronquer masquerait précisément les
        // retards qu'on vient y chercher.
        const rows = await maintenanceAPI.getPlans(truckId ? { truck_id: truckId } : {});
        if (perimee()) return undefined;
        const items = Array.isArray(rows) ? rows : [];
        setList({ items, total: items.length, pages: 1, tab: tabKey });
      } else {
        const fetchers = {
          readings: maintenanceAPI.getReadingsPage,
          workOrders: maintenanceAPI.getWorkOrdersPage,
          breakdowns: maintenanceAPI.getBreakdownsPage,
          documents: maintenanceAPI.getVehicleDocumentsPage,
          immobilizations: maintenanceAPI.getImmobilizationsPage,
        };
        const result = await fetchers[tabKey](serverParams);
        if (perimee()) return undefined;
        setList({ items: result.items, total: result.total, pages: result.pages, tab: tabKey });
      }
    } catch (error) {
      if (perimee()) return undefined;
      setListError(error);
      setList({ items: [], total: 0, pages: 1, tab: tabKey });
    } finally {
      if (!perimee()) {
        setListLoading(false);
        loadedTabs.current.add(tabKey);
      }
    }
    return undefined;
  }, [tabKey, serverParams, truckId, loadOverview, nouvelleDemande]);

  useEffect(() => {
    loadTrucks();
  }, [loadTrucks]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['maintenance', 'camions', 'documents', 'alertes', 'depenses'], (options) => Promise.all([loadKpis(options), loadList(options)]));


  // Un autre onglet ou filtre repart d'un tri neuf (la page, elle, est
  // remise à 1 par la pagination commune).
  useEffect(() => {
    setSort({ key: null, dir: 'asc' });
  }, [tabKey, truckId, statusValue, period, pageSize]);


  /* ---------------------------------------------------------------- */
  /* Actions                                                           */
  /* ---------------------------------------------------------------- */

  const truckById = useMemo(
    () => Object.fromEntries(trucks.map((truck) => [truck.id, truck])),
    [trucks],
  );
  const truckFilterOptions = useMemo(
    () => [{ value: 'all', label: 'Tous les camions' }, ...trucks.map(truckOption)],
    [trucks],
  );
  const truckLabel = useCallback(
    (id) => truckById[id]?.matricule || 'Camion inconnu',
    [truckById],
  );

  /**
   * Bouton d'un onglet vide.
   *
   * Chaque écriture de cet écran — relevé, plan, ordre, panne, échéance,
   * immobilisation — commence par choisir un camion. Tant que le parc est
   * vide, le formulaire s'ouvre sur un sélecteur sans option: le bouton mène
   * donc au parc, qui est le vrai prérequis, plutôt qu'à une impasse.
   */
  const emptyTabAction = useCallback(
    (label, Icon, onClick, allowed = true) => {
      if (!allowed) return undefined;
      const noFleet = trucks.length === 0;
      return (
        <Button
          size="sm"
          onClick={noFleet ? () => navigate('/trucks') : onClick}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {noFleet ? 'Ajouter un camion d’abord' : label}
        </Button>
      );
    },
    [trucks.length, navigate],
  );

  /**
   * Rechargement ciblé : une action ne réinvalide que ce qu'elle a touché.
   * Tout recharger après chaque geste faisait huit requêtes pour un
   * changement de statut, et repliait l'écran sous l'utilisateur.
   */
  const run = useCallback(
    async (id, action, successMessage, { refreshKpis = true } = {}) => {
      setBusyAction(id);
      try {
        const result = await action();
        await loadList();
        if (refreshKpis) loadKpis();
        toast.success(successMessage);
        return result;
      } catch (error) {
        toast.error(messageErreur(error, 'Action refusée'));
        throw error;
      } finally {
        setBusyAction(null);
      }
    },
    [loadList, loadKpis],
  );

  const applyKpiFilter = useCallback(
    (filter) => {
      const target = TAB_BY_KEY[filter.tab];
      if (!target) return;
      setStatusValue(filter.status || '');
      allerAPage(1);
      if (target.path !== location.pathname) navigate(target.path);
    },
    [navigate, location.pathname, allerAPage],
  );

  /* ---------------------------------------------------------------- */
  /* Recherche et tri, portés sur la page affichée                      */
  /* ---------------------------------------------------------------- */

  const searchable = useCallback(
    (row) =>
      [
        truckLabel(row.truck_id),
        row.order_number,
        row.title,
        row.name,
        row.reference,
        row.category,
        row.description,
        row.reason,
        row.workshop_name,
        row.note,
        DOC_TYPE_LABELS[row.doc_type],
        DOC_COMPONENT_LABELS[row.component],
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase(),
    [truckLabel],
  );

  const visibleRows = useMemo(() => {
    let rows = list.items;
    const term = debouncedSearch.trim().toLowerCase();
    if (term) rows = rows.filter((row) => searchable(row).includes(term));
    if (sort.key) {
      rows = [...rows].sort((a, b) => {
        const left = a[sort.key];
        const right = b[sort.key];
        if (left === right) return 0;
        if (left === null || left === undefined) return 1;
        if (right === null || right === undefined) return -1;
        const compared =
          typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right), 'fr');
        return sort.dir === 'asc' ? compared : -compared;
      });
    }
    return rows;
  }, [list.items, debouncedSearch, sort, searchable]);

  const toggleSort = (key) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );

  const hasFilters = Boolean(truckId || statusValue || period !== 'all' || debouncedSearch);
  const clearFilters = () => {
    setTruckId('');
    setStatusValue('');
    setPeriod('all');
    setSearch('');
  };

  /* --- série de coût pour la courbe ------------------------------- */
  const costSeries = useMemo(() => tabKey === 'overview' ? maintenanceSpendSeries(overviewData.workOrders, kpiPeriod) : [], [tabKey, overviewData.workOrders, kpiPeriod]);

  /* ---------------------------------------------------------------- */
  /* Rendu                                                             */
  /* ---------------------------------------------------------------- */

  const openDialog = (kind, record = null, prefill = null) =>
    setDialog({ kind, record, prefill });

  const listStateProps = {
    errorMessage: listError
      ? messageErreur(listError, 'Le serveur n’a pas répondu comme prévu.')
      : null,
    loading: listLoading,
    firstLoad,
    error: listError,
    hasFilters,
    onClearFilters: clearFilters,
    onRetry: loadList,
  };

  /* --- l'essentiel, en miniature dans l'en-tête --------------------- */
  const stat = (valeur) => (kpis && valeur !== undefined ? valeur : null);
  const statsEssentielles = [
    {
      label: 'Camions immobilisés',
      priorite: 1,
      valeur: stat(kpis?.immobilized_trucks),
      ton: kpis?.immobilized_trucks ? 'critique' : 'neutre',
      aide: 'Camions actuellement bloqués à l’atelier ou par un papier',
      onClick: () => applyKpiFilter({ tab: 'immobilizations', status: 'active' }),
    },
    {
      label: 'Pannes ouvertes',
      priorite: 2,
      valeur: stat(kpis?.open_breakdowns),
      ton: kpis?.open_breakdowns ? 'critique' : 'neutre',
      aide: 'Pannes non encore prises en compte',
      onClick: () => applyKpiFilter({ tab: 'breakdowns', status: 'open' }),
    },
    {
      label: 'Échéances à traiter',
      priorite: 3,
      valeur: stat(kpis?.upcoming_deadlines_count),
      ton: kpis?.upcoming_deadlines_count ? 'attention' : 'neutre',
      aide: 'Plans et papiers dans la fenêtre d’alerte',
      onClick: () => applyKpiFilter({ tab: 'documents' }),
    },
  ];
  const alertesPage = kpiError
    ? [
        {
          niveau: 'attention',
          court: 'Chiffres indisponibles',
          texte:
            'Les chiffres du parc n’ont pas pu être calculés. Les tableaux ci-dessous restent à jour.',
          action: { label: 'Réessayer', onClick: loadKpis },
        },
      ]
    : [];

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHero
        accroche="Un parc fiable pour aller plus loin"
        illustration="conformite"
        statsDisposition="grille"
        actionsDisposition="grille"
        eyebrow="Parc & atelier"
        title="Entretien et pannes"
        stats={statsEssentielles}
        statsPlus={
          <PlusIndicateurs
            kpis={kpis}
            loading={kpiLoading}
            erreur={kpiError}
            onRetry={loadKpis}
            periodDays={kpiPeriod}
            onSelect={applyKpiFilter}
          />
        }
        alertes={alertesPage}
        actions={
          <>
            {/* L'atelier a besoin de la liste papier des immobilisés: elle est
                dans le parc par état, et elle se lit sans droit d'écriture. */}
            <PrintMenu
              label="Imprimer"
              items={[
                {
                  label: 'Parc par état',
                  filename: 'parc-par-etat',
                  url: printingAPI.trucksByStatus,
                  formats: ['pdf', 'xlsx', 'csv'],
                  params: { include_rented: true },
                },
              ]}
            />
            {canWrite && (
            <>
              <Button
                variant="outline"
                size="sm"
                title="Ouvrir les préventifs échus"
                disabled={busyAction === 'generate'}
                onClick={() =>
                  run(
                    'generate',
                    () => maintenanceAPI.generateDueWorkOrders(),
                    'Ordres préventifs échus ouverts',
                  ).catch(() => {})
                }
              >
                <Wrench className="h-4 w-4" aria-hidden="true" />
                {busyAction === 'generate' ? 'Ouverture…' : 'Préventifs échus'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                title="Contrôler les échéances"
                disabled={busyAction === 'enforce'}
                onClick={() =>
                  run(
                    'enforce',
                    () => maintenanceAPI.enforceDocumentDeadlines(),
                    'Échéances documentaires contrôlées',
                  ).catch(() => {})
                }
              >
                <ShieldAlert className="h-4 w-4" aria-hidden="true" />
                {busyAction === 'enforce' ? 'Contrôle…' : 'Échéances'}
              </Button>
              <Button size="sm" title="Immobiliser un camion" onClick={() => openDialog('immobilization')}>
                <Citerne className="h-4 w-4" aria-hidden="true" />
                Immobiliser
              </Button>
            </>
            )}
          </>
        }
      >
        <MaintenanceSpendHeader
          kpis={kpis} loading={kpiLoading} error={kpiError}
          periodDays={kpiPeriod} onPeriodChange={setKpiPeriod}
          onOpenOrders={() => applyKpiFilter({ tab: 'workOrders' })}
          series={costSeries} truncated={overviewData.workOrdersTruncated}
          showTrend={tabKey === 'overview'}
        />
      </PageHero>

      <Tabs
        value={tabKey}
        onValueChange={(value) => {
          // Changer d'onglet à la main repart d'une liste non filtrée : le
          // filtre de statut de l'onglet quitté n'a pas de sens sur le suivant.
          setStatusValue('');
          navigate(TAB_BY_KEY[value].path);
        }}
        className="space-y-3"
      >
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 p-1">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              /* L'onglet actif prend la flamme pâle : c'est le seul repère de
                 position de l'écran, et il se lit sans concurrencer les
                 couleurs d'état des tuiles et des badges — qui, elles, disent
                 quelque chose du parc. `flame-ink` est la variante encrée du
                 jeton, la seule qui porte du texte. */
              className="data-[state=active]:bg-flame/15 data-[state=active]:text-flame-ink data-[state=active]:shadow-none"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabKey !== 'overview' && (
          <RegistryToolbar>
            <div className="relative min-w-[200px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
                aria-hidden="true"
              />
              <Input
                className="pl-9"
                placeholder="Filtrer les lignes affichées…"
                aria-label="Filtrer les lignes affichées"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <SearchableSelect
              className="w-[170px]"
              aria-label="Filtrer par camion"
              placeholder="Tous les camions"
              searchPlaceholder="Rechercher une immatriculation…"
              emptyMessage="Aucun camion trouvé"
              value={truckId || 'all'}
              onValueChange={(v) => setTruckId(v === 'all' ? '' : v)}
              options={truckFilterOptions}
            />

            {activeTab.statusFilter && (
              <Select
                value={statusValue || 'all'}
                onValueChange={(v) => setStatusValue(v === 'all' ? '' : v)}
              >
                <SelectTrigger
                  className="w-[170px]"
                  aria-label={`Filtrer par ${activeTab.statusFilter.label.toLowerCase()}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{activeTab.statusFilter.label} : tout</SelectItem>
                  {Object.entries(activeTab.statusFilter.options).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {tabKey !== 'plans' && (
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[170px]" aria-label="Filtrer par période">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PERIODS).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4" aria-hidden="true" />
                Effacer
              </Button>
            )}

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadList}
                disabled={listLoading}
                aria-label="Recharger la liste"
              >
                <RefreshCw
                  className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
              </Button>
              {canWrite && CREATE_ACTIONS[tabKey] && (
                <Button size="sm" onClick={() => openDialog(CREATE_ACTIONS[tabKey].kind)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {CREATE_ACTIONS[tabKey].label}
                </Button>
              )}
              {!canWrite && tabKey === 'breakdowns' && canReportBreakdown && (
                <Button size="sm" onClick={() => openDialog('breakdown')}>
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  Signaler une panne
                </Button>
              )}
            </div>
          </RegistryToolbar>
        )}

        {/* ---------------------------------------------------------- */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            {...listStateProps}
            data={overviewData}
            truckLabel={truckLabel}
            canWrite={canWrite}
            busyAction={busyAction}
            onRelease={setReleaseTarget}
            onOpenTruck={(id) =>
              navigate(`/trucks/${id}?tab=maintenance`, {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            onGoTo={(key) => navigate(TAB_BY_KEY[key].path)}
          />
        </TabsContent>

        <TabsContent value="readings">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucun relevé compteur"
            emptyDescription="Sans relevé, aucun plan préventif ne peut tomber à échéance."
            emptyAction={emptyTabAction(
              'Saisir un relevé',
              Plus,
              () => openDialog('reading'),
              canWrite,
            )}
            columns={[
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink id={row.truck_id} label={truckLabel(row.truck_id)} />
                ),
              },
              {
                key: 'value',
                label: 'Compteur',
                sortable: true,
                render: (row) => (
                  <span className="font-mono tabular font-semibold">
                    {counter(row.value, row.reading_type)}
                  </span>
                ),
              },
              {
                key: 'reading_date',
                label: 'Relevé le',
                sortable: true,
                render: (row) => dateTime(row.reading_date),
              },
              {
                key: 'source',
                label: 'Origine',
                render: (row) => (
                  <Badge variant="muted">
                    {row.source === 'driver_app' ? 'Application chauffeur' : 'Saisie atelier'}
                  </Badge>
                ),
              },
              {
                key: 'note',
                label: 'Note',
                render: (row) => row.note || '—',
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="plans">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucun plan préventif"
            emptyDescription="Sans plan, les entretiens ne sont jamais annoncés à l’avance."
            emptyAction={emptyTabAction(
              'Créer un plan',
              Plus,
              () => openDialog('plan'),
              canWrite,
            )}
            columns={[
              {
                key: 'name',
                label: 'Entretien',
                sortable: true,
                render: (row) => (
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">{row.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {TRIGGER_LABELS[row.trigger_type]}
                    </p>
                  </div>
                ),
              },
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink id={row.truck_id} label={truckLabel(row.truck_id)} />
                ),
              },
              {
                key: 'next_due_km',
                label: 'Prochaine échéance',
                sortable: true,
                render: (row) =>
                  [
                    row.next_due_km ? `${num(row.next_due_km).toLocaleString('fr-FR')} km` : null,
                    row.next_due_date ? `au ${dateLabel(row.next_due_date)}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—',
              },
              {
                key: 'due_state',
                label: 'État',
                sortable: true,
                render: (row) => (
                  <div className="space-y-1">
                    <StateBadge
                      variant={
                        row.due_state === 'overdue'
                          ? 'destructive'
                          : row.due_state === 'due_soon'
                            ? 'warning'
                            : 'success'
                      }
                      label={
                        row.due_state === 'overdue'
                          ? 'En retard'
                          : row.due_state === 'due_soon'
                            ? 'À prévoir'
                            : 'À jour'
                      }
                    />
                    {row.due_reason && (
                      <p className="text-xs text-muted-foreground">{row.due_reason}</p>
                    )}
                  </div>
                ),
              },
              {
                key: 'estimated_cost',
                label: 'Budget',
                sortable: true,
                render: (row) => (row.estimated_cost ? money(row.estimated_cost) : '—'),
              },
              canWrite && {
                key: 'actions',
                label: '',
                render: (row) => (
                  <Button size="sm" variant="outline" onClick={() => openDialog('plan', row)}>
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                    Modifier
                  </Button>
                ),
              },
            ].filter(Boolean)}
          />
        </TabsContent>

        <TabsContent value="workOrders">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucun ordre de travail"
            emptyDescription="Les interventions d’atelier se tracent ici, avec leur coût réel."
            emptyAction={emptyTabAction(
              'Ouvrir un ordre',
              Plus,
              () => openDialog('workOrder'),
              canWrite,
            )}
            columns={[
              {
                key: 'order_number',
                label: 'Ordre',
                sortable: true,
                render: (row) => (
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-muted-foreground">
                      {referenceLisible(row.order_number)}
                    </p>
                    <p className="font-semibold text-foreground">{row.title}</p>
                  </div>
                ),
              },
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink id={row.truck_id} label={truckLabel(row.truck_id)} />
                ),
              },
              {
                key: 'status',
                label: 'Statut',
                sortable: true,
                render: (row) => (
                  <StateBadge
                    variant={
                      row.status === 'done'
                        ? 'success'
                        : row.status === 'cancelled'
                          ? 'muted'
                          : row.priority === 'critical'
                            ? 'destructive'
                            : 'info'
                    }
                    label={WORK_STATUS_LABELS[row.status]}
                  />
                ),
              },
              {
                key: 'work_type',
                label: 'Nature',
                sortable: true,
                render: (row) => WORK_TYPE_LABELS[row.work_type],
              },
              {
                key: 'workshop_name',
                label: 'Atelier',
                sortable: true,
                render: (row) => row.workshop_name || 'Atelier interne',
              },
              {
                key: 'scheduled_date',
                label: 'Prévu le',
                sortable: true,
                render: (row) => dateLabel(row.scheduled_date),
              },
              {
                key: 'total_cost',
                label: 'Coût',
                sortable: true,
                render: (row) => (
                  <span className="font-mono tabular">{money(row.total_cost)}</span>
                ),
              },
              {
                key: 'actions',
                label: '',
                render: (row) => (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPartsTarget(row)}>
                      <Package className="h-4 w-4" aria-hidden="true" />
                      Pièces
                    </Button>
                    {canWrite && !['done', 'cancelled'].includes(row.status) && (
                      <>
                        {row.status !== 'in_progress' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyAction === `start-${row.id}`}
                            onClick={() =>
                              run(
                                `start-${row.id}`,
                                () =>
                                  maintenanceAPI.updateWorkOrder(row.id, {
                                    status: 'in_progress',
                                  }),
                                'Ordre passé en cours',
                              ).catch(() => {})
                            }
                          >
                            {busyAction === `start-${row.id}` ? 'Passage…' : 'Passer en cours'}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openDialog('workOrder', row)}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button size="sm" onClick={() => setCloseTarget(row)}>
                          Clôturer
                        </Button>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="breakdowns">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucune panne signalée"
            emptyDescription="Les pannes remontées du terrain et de l’application chauffeur arrivent ici."
            emptyAction={emptyTabAction(
              'Signaler une panne',
              AlertTriangle,
              () => openDialog('breakdown'),
              canReportBreakdown,
            )}
            columns={[
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink id={row.truck_id} label={truckLabel(row.truck_id)} />
                ),
              },
              {
                key: 'category',
                label: 'Organe',
                sortable: true,
                render: (row) => (
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">
                      {texteSur(row.category, 'Non précisé')}
                    </p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {row.description}
                    </p>
                  </div>
                ),
              },
              {
                key: 'severity',
                label: 'Gravité',
                sortable: true,
                render: (row) => (
                  <StateBadge
                    variant={
                      row.severity === 'critical'
                        ? 'destructive'
                        : row.severity === 'major'
                          ? 'warning'
                          : 'muted'
                    }
                    label={SEVERITY_LABELS[row.severity]}
                  />
                ),
              },
              {
                key: 'status',
                label: 'Statut',
                sortable: true,
                render: (row) => (
                  <Badge variant="outline">
                    {BREAKDOWN_STATUS_LABELS[row.status]}
                  </Badge>
                ),
              },
              {
                key: 'reported_at',
                label: 'Signalée le',
                sortable: true,
                render: (row) => dateTime(row.reported_at),
              },
              canWrite && {
                key: 'actions',
                label: '',
                render: (row) => (
                  <div className="flex flex-wrap items-center gap-2">
                    {row.status === 'open' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyAction === `ack-${row.id}`}
                        onClick={() =>
                          run(
                            `ack-${row.id}`,
                            () =>
                              maintenanceAPI.updateBreakdown(row.id, {
                                status: 'acknowledged',
                              }),
                            'Panne prise en compte',
                          ).catch(() => {})
                        }
                      >
                        {busyAction === `ack-${row.id}` ? 'Envoi…' : 'Prendre en compte'}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openDialog('breakdown', row)}>
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    {!['converted', 'closed'].includes(row.status) && (
                      <Button
                        size="sm"
                        disabled={busyAction === `convert-${row.id}`}
                        onClick={() =>
                          run(
                            `convert-${row.id}`,
                            () =>
                              maintenanceAPI.createWorkOrder({
                                truck_id: row.truck_id,
                                title: `Réparation : ${row.category || 'panne signalée'}`,
                                description: row.description,
                                work_type: 'corrective',
                                priority: row.severity === 'critical' ? 'critical' : 'high',
                                breakdown_id: row.id,
                                immobilizes: row.severity === 'critical',
                              }),
                            'Ordre de travail ouvert depuis la panne',
                          ).catch(() => {})
                        }
                      >
                        {busyAction === `convert-${row.id}` ? 'Ouverture…' : 'Ouvrir l’ordre'}
                      </Button>
                    )}
                  </div>
                ),
              },
            ].filter(Boolean)}
          />
        </TabsContent>

        <TabsContent value="documents">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucune échéance suivie"
            emptyDescription="Assurance et visite technique expireront sans prévenir tant qu’elles ne sont pas enregistrées."
            emptyAction={emptyTabAction(
              'Ajouter une échéance',
              Plus,
              () => openDialog('document'),
              canWrite,
            )}
            columns={[
              {
                key: 'doc_type',
                label: 'Document',
                sortable: true,
                render: (row) => (
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">
                      {DOC_TYPE_LABELS[row.doc_type]}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {row.reference || 'Sans référence'}
                      {row.document_id ? ' · scan joint' : ''}
                    </p>
                  </div>
                ),
              },
              {
                key: 'component',
                label: 'Élément',
                render: (row) => (
                  <span data-testid={`document-component-${row.id}`}>
                    {DOC_COMPONENT_LABELS[row.component] || '—'}
                  </span>
                ),
              },
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink
                    id={row.truck_id}
                    label={truckLabel(row.truck_id)}
                    tab="documents"
                  />
                ),
              },
              {
                key: 'expiry_date',
                label: 'Expire le',
                sortable: true,
                render: (row) => dateLabel(row.expiry_date),
              },
              {
                key: 'expiry_state',
                label: 'État',
                sortable: true,
                render: (row) => (
                  <div className="space-y-1">
                    <StateBadge
                      variant={
                        row.expiry_state === 'expired'
                          ? 'destructive'
                          : row.expiry_state === 'expiring'
                            ? 'warning'
                            : 'success'
                      }
                      label={
                        row.expiry_state === 'expired'
                          ? 'Expiré'
                          : row.expiry_state === 'expiring'
                            ? 'Bientôt'
                            : 'Valide'
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {row.expiry_state === 'expired'
                        ? `Dépassé de ${Math.abs(num(row.days_remaining))} j`
                        : `Encore ${num(row.days_remaining)} j`}
                    </p>
                  </div>
                ),
              },
              {
                key: 'blocks_assignment',
                label: 'Portée',
                render: (row) => (
                  <Badge variant={row.blocks_assignment ? 'warning' : 'muted'}>
                    {row.blocks_assignment ? 'Bloque l’affectation' : 'Alerte seulement'}
                  </Badge>
                ),
              },
              canWrite && {
                key: 'actions',
                label: '',
                render: (row) => (
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDialog('document', row)}
                      aria-label="Corriger cette échéance"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                      Corriger
                    </Button>
                    {/* Un renouvellement est un nouveau papier du même camion,
                        de la même nature et du même élément: le serveur
                        désactive l'ancien, qui ne déclenche plus d'alerte. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openDialog('document', null, {
                          truck_id: row.truck_id,
                          doc_type: row.doc_type,
                          component: row.component || NO_COMPONENT,
                          issuer: row.issuer || '',
                          alert_days_before:
                            row.alert_days_before != null ? String(row.alert_days_before) : '30',
                          blocks_assignment: Boolean(row.blocks_assignment),
                          cost_bearer: row.cost_bearer || '',
                        })
                      }
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Renouveler
                    </Button>
                  </div>
                ),
              },
            ].filter(Boolean)}
          />
        </TabsContent>

        <TabsContent value="immobilizations">
          <RecordsTable
            {...listStateProps}
            rows={visibleRows}
            sort={sort}
            onSort={toggleSort}
            emptyTitle="Aucune immobilisation"
            emptyDescription="Tout le parc est affectable sur la période et les filtres retenus."
            emptyAction={emptyTabAction(
              'Immobiliser un camion',
              Citerne,
              () => openDialog('immobilization'),
              canWrite,
            )}
            columns={[
              {
                key: 'truck_id',
                label: 'Camion',
                sortable: true,
                render: (row) => (
                  <TruckLink id={row.truck_id} label={truckLabel(row.truck_id)} />
                ),
              },
              {
                key: 'reason',
                label: 'Motif',
                render: (row) => (
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">{row.reason}</p>
                    <p className="text-xs text-muted-foreground">
                      {REASON_CODE_LABELS[row.reason_code]}
                    </p>
                  </div>
                ),
              },
              {
                key: 'status',
                label: 'Statut',
                sortable: true,
                render: (row) => (
                  <StateBadge
                    variant={row.status === 'active' ? 'destructive' : 'success'}
                    label={IMMO_STATUS_LABELS[row.status]}
                  />
                ),
              },
              {
                key: 'started_at',
                label: 'Depuis le',
                sortable: true,
                render: (row) => dateTime(row.started_at),
              },
              {
                key: 'released_at',
                label: 'Levée le',
                sortable: true,
                render: (row) => (row.released_at ? dateTime(row.released_at) : '—'),
              },
              canWrite && {
                key: 'actions',
                label: '',
                render: (row) =>
                  row.status === 'active' ? (
                    <Button size="sm" onClick={() => setReleaseTarget(row)}>
                      Lever
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {row.release_note || '—'}
                    </span>
                  ),
              },
            ].filter(Boolean)}
          />
        </TabsContent>
      </Tabs>

      {tabKey !== 'overview' && tabKey !== 'plans' && (
        <>
          {debouncedSearch && list.total > 0 && (
            <p className="text-sm text-muted-foreground">
              {accorde(visibleRows.length, 'ligne affichée', 'lignes affichées')} après filtre de texte
            </p>
          )}
          <BarrePagination pagination={pagination} nom="lignes" />
        </>
      )}

      {/* ---------------------------------------------------------- */}
      {/* Dialogues                                                   */}
      {/* ---------------------------------------------------------- */}

      {dialog && (
        <RecordDialog
          spec={FORMS[dialog.kind]}
          record={dialog.record}
          prefill={dialog.prefill}
          trucks={trucks}
          onClose={() => setDialog(null)}
          onSubmit={async (payload, editing) => {
            const handlers = {
              reading: { create: (p) => maintenanceAPI.createReading(p) },
              plan: {
                create: (p) => maintenanceAPI.createPlan(p),
                update: (id, p) => maintenanceAPI.updatePlan(id, p),
              },
              workOrder: {
                create: (p) => maintenanceAPI.createWorkOrder(p),
                update: (id, p) => maintenanceAPI.updateWorkOrder(id, p),
              },
              breakdown: {
                create: (p) => maintenanceAPI.reportBreakdown(p),
                update: (id, p) => maintenanceAPI.updateBreakdown(id, p),
              },
              document: {
                create: (p) => maintenanceAPI.createVehicleDocument(p),
                update: (id, p) => maintenanceAPI.updateVehicleDocument(id, p),
              },
              immobilization: { create: (p) => maintenanceAPI.immobilize(p) },
            }[dialog.kind];

            await run(
              'dialog',
              () =>
                editing
                  ? handlers.update(dialog.record.id, payload)
                  : handlers.create(payload),
              editing ? 'Modification enregistrée' : 'Enregistré',
            );
            setDialog(null);
          }}
        />
      )}

      {releaseTarget && (
        <ReleaseDialog
          record={releaseTarget}
          truckLabel={truckLabel(releaseTarget.truck_id)}
          onClose={() => setReleaseTarget(null)}
          onConfirm={async (note) => {
            await run(
              'release',
              () => maintenanceAPI.releaseImmobilization(releaseTarget.id, note),
              'Immobilisation levée',
            );
            setReleaseTarget(null);
          }}
        />
      )}

      {closeTarget && (
        <CloseWorkOrderDialog
          record={closeTarget}
          truckLabel={truckLabel(closeTarget.truck_id)}
          onClose={() => setCloseTarget(null)}
          onConfirm={(payload) =>
            run(
              'close',
              () => maintenanceAPI.closeWorkOrder(closeTarget.id, payload),
              'Ordre clôturé',
            )
          }
        />
      )}

      {partsTarget && (
        <PartsDialog
          record={partsTarget}
          canWrite={canWrite}
          onClose={() => setPartsTarget(null)}
          onAdded={() => {
            loadList();
            loadKpis();
          }}
        />
      )}
    </div>
  );
}

const CREATE_ACTIONS = {
  readings: { kind: 'reading', label: 'Nouveau relevé' },
  plans: { kind: 'plan', label: 'Nouveau plan' },
  workOrders: { kind: 'workOrder', label: 'Nouvel ordre' },
  breakdowns: { kind: 'breakdown', label: 'Signaler une panne' },
  documents: { kind: 'document', label: 'Nouvelle échéance' },
  immobilizations: { kind: 'immobilization', label: 'Immobiliser' },
};

/* ------------------------------------------------------------------ */
/* Vue d'ensemble                                                      */
/* ------------------------------------------------------------------ */

function OverviewTab({
  data,
  loading,
  firstLoad,
  error,
  errorMessage,
  onRetry,
  truckLabel,
  canWrite,
  onRelease,
  onOpenTruck,
  onGoTo,
}) {
  /**
   * Le tri par motif reste local, et volontairement pas dans l'URL: « Ce qui
   * bloque » se lit au comptoir — on sépare d'un geste ce qui est atelier de
   * ce qui est paperasse —, ce n'est pas un état d'écran qu'on partage par
   * lien. L'onglet « Immobilisations », lui, garde ses filtres serveur.
   */
  const [motif, setMotif] = useState('all');

  if (loading && firstLoad) return <LoadingState shape="cards" count={4} />;

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Le parc n’a pas pu être chargé"
        description={`${errorMessage || 'Le serveur n’a pas répondu.'} Ce qui s’affiche ne reflète pas l’état réel des camions : réessayez avant de décider d’une affectation.`}
        actionLabel="Réessayer"
        onAction={onRetry}
      />
    );
  }

  const duePlans = data.plans.filter((plan) => plan.due_state !== 'ok');
  const openOrders = data.workOrders.filter(
    (order) => !['done', 'cancelled'].includes(order.status),
  );
  const immobilisations =
    motif === 'all'
      ? data.immobilizations
      : data.immobilizations.filter((item) => item.reason_code === motif);

  return (
    <div className="space-y-4">
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <h2>Ce qui bloque</h2>
              <Aide titre="Ce qui bloque">Les éléments qui empêchent un camion d’être affecté.</Aide>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={motif} onValueChange={setMotif}>
              <SelectTrigger
                className="w-[210px]"
                aria-label="Filtrer par motif d’immobilisation"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les immobilisations</SelectItem>
                {Object.entries(REASON_CODE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Le filtre ne remplace pas l'onglet: il montre les
                immobilisations en cours, l'onglet porte aussi les levées. */}
            <Button variant="ghost" size="sm" onClick={() => onGoTo('immobilizations')}>
              Ouvrir l’onglet
            </Button>
          </div>
        </div>

        {immobilisations.length === 0 ? (
          // Vert: c'est un état du parc, pas une décoration de bloc. Rien ne
          // bloque, et c'est la seule bonne nouvelle que cet écran ait à
          // donner — elle mérite d'être lisible d'un coup d'œil.
          <div className="flex items-start gap-3 rounded-2xl border border-success/30 bg-success/[0.07] px-4 py-3.5">
            <CheckCircle2
              className="mt-0.5 h-5 w-5 shrink-0 text-success"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="font-semibold text-foreground">
                {motif === 'all'
                  ? 'Aucun camion immobilisé'
                  : 'Aucune immobilisation pour ce motif'}
              </p>
              <p className="flex items-center gap-1 text-sm text-muted-foreground">
                {motif === 'all' ? (
                  'Tout le parc est affectable.'
                ) : (
                  <>
                    Aucun camion bloqué pour ce motif.
                    <Aide titre="Autres motifs">
                      D’autres motifs peuvent encore bloquer des camions : revenez à « Toutes les immobilisations ».
                    </Aide>
                  </>
                )}
              </p>
            </div>
          </div>
        ) : (
          <BlocagesParCamion
            items={immobilisations}
            truckLabel={truckLabel}
            canWrite={canWrite}
            onRelease={onRelease}
            onOpenTruck={onOpenTruck}
          />
        )}
      </section>

      {/* grid-cols-1 (minmax(0, 1fr)) : sans lui, une ligne tronquée
          élargissait la colonne au-delà de l'écran sur téléphone. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <OverviewList
          title="Échéances à traiter"
          description="Entretiens qui basculeront en blocage s’ils sont laissés."
          icon={CalendarClock}
          onMore={() => onGoTo('plans')}
          moreLabel="Tous les plans"
          rows={duePlans.slice(0, 8)}
          renderRow={(plan) => ({
            key: plan.id,
            title: `${plan.name} · ${truckLabel(plan.truck_id)}`,
            meta: plan.due_reason,
            badge: (
              <StateBadge
                variant={plan.due_state === 'overdue' ? 'destructive' : 'warning'}
                label={plan.due_state === 'overdue' ? 'En retard' : 'À prévoir'}
              />
            ),
          })}
          emptyTitle="Aucune échéance dans la fenêtre d’alerte."
          emptyDescription="Les plans préventifs s’afficheront ici dès qu’ils approcheront de leur seuil, le temps de réserver un créneau atelier."
        />

        <OverviewList
          title="Atelier en cours"
          description="Interventions ouvertes, du brouillon à la clôture."
          icon={Wrench}
          onMore={() => onGoTo('workOrders')}
          moreLabel="Tous les ordres"
          rows={openOrders.slice(0, 8)}
          renderRow={(order) => ({
            key: order.id,
            title: `${referenceLisible(order.order_number)} · ${order.title}`,
            meta: `${truckLabel(order.truck_id)} · ${order.workshop_name || 'Atelier interne'} · ${money(order.total_cost)}`,
            badge: (
              <StateBadge
                variant={order.priority === 'critical' ? 'destructive' : 'info'}
                label={WORK_STATUS_LABELS[order.status]}
              />
            ),
          })}
          emptyTitle="Aucune intervention ouverte."
          emptyDescription="Les ordres de travail s’afficheront ici du brouillon à la clôture, avec l’atelier et le coût engagé."
        />
      </div>
    </div>
  );
}

/**
 * Les deux cartes jumelles du bas d'écran.
 *
 * Elles passent par `SectionCard` — la même carte que l'accueil — pour que
 * leurs deux en-têtes tombent à la même hauteur: c'est précisément ce qui
 * manquait quand chaque bloc posait son titre à sa façon, et deux cartes
 * côte à côte qui ne s'alignent pas se lisent comme deux morceaux d'écrans
 * différents.
 */
function OverviewList({
  title,
  description,
  icon: Icon,
  rows,
  renderRow,
  emptyTitle,
  emptyDescription,
  onMore,
  moreLabel,
}) {
  return (
    <SectionCard
      icon={Icon}
      title={
        <span className="inline-flex items-center gap-1">
          {title}
          {description && <Aide titre={title}>{description}</Aide>}
        </span>
      }
      actionLabel={moreLabel}
      onAction={onMore}
      contentClassName="pt-2"
    >
      {rows.length === 0 ? (
        // Vide n'est pas la même chose que vide de sens: ici il n'y a rien à
        // traiter, et l'illustration « tout est à jour » le dit avant la
        // phrase. Le liseré vert garde la couleur du côté de l'état.
        <EmptyState
          variant="empty"
          illustration="ajour"
          title={emptyTitle}
          description={emptyDescription}
          className="border-solid border-success/30 bg-success/[0.05] px-4 py-6"
        />
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((row) => {
            const view = renderRow(row);
            return (
              <div
                key={view.key}
                className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{view.title}</p>
                  {view.meta && (
                    <p className="truncate text-xs text-muted-foreground">{view.meta}</p>
                  )}
                </div>
                <div className="self-start sm:self-auto">{view.badge}</div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ */
/* Indicateurs de parc, en miniature dans l'en-tête                    */
/* ------------------------------------------------------------------ */

/**
 * Les chiffres secondaires, ouverts depuis « Plus d'indicateurs ».
 *
 * Ils viennent de `GET /maintenance/kpis`, jamais des listes affichées: un
 * taux d'immobilisation déduit d'une page de cinquante lignes serait faux.
 * Chaque ligne est un point d'entrée vers ce qu'elle résume.
 */
function PlusIndicateurs({ kpis, loading, erreur, onRetry, periodDays, onSelect }) {
  const k = kpis || {};
  const pick = (filter) => (onSelect ? () => onSelect(filter) : undefined);
  const lignes = [
    {
      label: 'Ordres en cours',
      valeur: mesure(k.open_work_orders),
      aide: 'Interventions ouvertes à l’atelier',
      onClick: pick({ tab: 'workOrders', status: 'in_progress' }),
    },
    {
      label: 'Taux d’immobilisation',
      valeur: `${mesure(k.immobilization_rate, 1)} %`,
      aide: `${mesure(k.immobilized_hours)} h à l’arrêt`,
      onClick: pick({ tab: 'immobilizations', status: 'active' }),
    },
    {
      label: 'Coût au kilomètre',
      valeur: `${mesure(k.cost_per_km, 1)} FCFA/km`,
      aide: k.distance_km ? `${mesure(k.distance_km)} km parcourus` : 'Aucun relevé sur la période',
      onClick: pick({ tab: 'workOrders' }),
    },
    {
      label: 'Pannes sur la période',
      valeur: mesure(k.breakdowns_in_period),
      aide: 'Toutes les pannes sur la période sélectionnée',
      onClick: pick({ tab: 'breakdowns' }),
    },
    {
      label: 'Distance entre pannes',
      valeur: `${mesure(k.mtbf_km)} km`,
      aide: k.mtbf_days ? `soit ${mesure(k.mtbf_days)} jours` : 'Aucune panne relevée',
      onClick: pick({ tab: 'breakdowns' }),
    },
    {
      label: 'Durée de remise en route',
      valeur: `${mesure(k.mttr_hours, 1)} h`,
      aide: 'Moyenne des immobilisations levées',
      onClick: pick({ tab: 'immobilizations', status: 'released' }),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="readout-label">Parc sur {FENETRES[periodDays] || `${periodDays} jours`}</p>
      </div>
      {erreur ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Les indicateurs du parc n’ont pas pu être calculés.
          </p>
          <Button size="sm" variant="outline" onClick={onRetry}>
            Réessayer
          </Button>
        </div>
      ) : (
        <dl className="divide-y divide-border/60">
          {lignes.map((ligne) => (
            <div key={ligne.label}>
              <button
                type="button"
                onClick={ligne.onClick}
                title={ligne.aide}
                className="flex w-full items-baseline justify-between gap-3 py-1.5 text-left text-sm hover:text-foreground"
              >
                <dt className="text-muted-foreground">{ligne.label}</dt>
                <dd className="font-mono text-base font-semibold tabular-nums text-foreground">
                  {loading ? '…' : ligne.valeur}
                </dd>
              </button>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tableau générique                                                   */
/* ------------------------------------------------------------------ */

function RecordsTable({
  rows,
  columns,
  loading,
  firstLoad,
  error,
  hasFilters,
  onClearFilters,
  onRetry,
  errorMessage,
  emptyTitle,
  emptyDescription,
  emptyAction,
  sort,
  onSort,
}) {
  if (loading && firstLoad) return <LoadingState shape="table" columns={columns.length} />;

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="La liste n’a pas pu être chargée"
        description={`${errorMessage || 'Le chargement a échoué.'} Rien de ce qui suit ne doit être pris pour l’état réel du parc.`}
        actionLabel="Réessayer"
        onAction={onRetry}
      />
    );
  }

  if (rows.length === 0) {
    return hasFilters ? (
      <EmptyState
        variant="filtered"
        title="Aucune ligne ne correspond"
        description="Les filtres actifs masquent tout le contenu de cette page."
        actionLabel="Effacer les filtres"
        onAction={onClearFilters}
      />
    ) : (
      <EmptyState
        variant="empty"
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction || undefined}
      />
    );
  }

  return (
    <div
      className={`overflow-hidden rounded-xl border border-border bg-card transition-opacity ${
        loading ? 'opacity-60' : ''
      }`}
      aria-busy={loading}
    >
      <Table stickyFirstColumn>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.key}>
                {column.sortable ? (
                  <button
                    type="button"
                    onClick={() => onSort(column.key)}
                    className="inline-flex items-center gap-1 rounded uppercase tracking-[0.06em] hover:text-foreground"
                    aria-label={`Trier par ${column.label}`}
                  >
                    {column.label}
                    <ArrowUpDown
                      className={`h-3 w-3 ${sort.key === column.key ? 'text-foreground' : 'opacity-40'}`}
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  column.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {columns.map((column) => (
                <TableCell key={column.key}>{column.render(row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Formulaire piloté par description                                   */
/* ------------------------------------------------------------------ */

function Field({ id, label, hint, error, children }) {
  // Une aide de champ de huit mots ou moins reste sous le champ; au-delà,
  // elle passe derrière une icône d'aide à côté du libellé.
  const hintCourt = typeof hint === 'string' && hint.trim().split(/\s+/).length <= 8;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <Label htmlFor={id}>{label}</Label>
        {hint && !hintCourt && <Aide titre={typeof label === 'string' ? label : 'Aide'}>{hint}</Aide>}
      </div>
      {children}
      {hint && hintCourt && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      <FieldError show={Boolean(error)}>{error}</FieldError>
    </div>
  );
}

function RecordDialog({ spec, record, prefill, trucks, onClose, onSubmit }) {
  const editing = Boolean(record);
  const [form, setForm] = useState(() => {
    if (!record) return prefill ? { ...spec.initial, ...prefill } : spec.initial;
    // Les valeurs numériques repassent en texte : la conversion à chaque
    // frappe empêche de taper « 12.5 », le point disparaissant aussitôt.
    const next = { ...spec.initial };
    Object.keys(next).forEach((key) => {
      const value = record[key];
      if (value === null || value === undefined) return;
      next[key] = typeof next[key] === 'boolean' ? Boolean(value) : String(value);
    });
    if (record.expiry_date) next.expiry_date = String(record.expiry_date).slice(0, 10);
    if (record.issued_date) next.issued_date = String(record.issued_date).slice(0, 10);
    if (record.scheduled_date) next.scheduled_date = String(record.scheduled_date).slice(0, 10);
    return next;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const truckOptions = useMemo(() => trucks.map(truckOption), [trucks]);

  // « Qui paie ? » : proposé seulement si la location de camions est active et
  // que le camion choisi est sous contrat chez un transporteur. Le choix est
  // pré-rempli d'après le contrat, puis reste modifiable (jamais réécrasé
  // après une saisie de l'utilisateur).
  const { hasModule } = useAuth();
  const rentalsActive = typeof hasModule === 'function' && hasModule('truck_rentals');
  const bearerField = spec.fields.find((field) => field.type === 'bearer');
  const bearerCategory = bearerField ? bearerField.category(form) : null;
  const bearerDate = form.scheduled_date || form.issued_date || '';
  const bearerTruckId = form.truck_id || '';
  const [bearerInfo, setBearerInfo] = useState(null);
  const bearerTouched = useRef(false);

  useEffect(() => {
    // Un camion sans contrat n'a pas de choix à faire: la valeur d'un camion
    // précédent ne doit pas partir avec la saisie (champ masqué).
    const clearHidden = () => {
      if (!editing) setForm((current) => (current.cost_bearer ? { ...current, cost_bearer: '' } : current));
    };
    if (!rentalsActive || !bearerField || !bearerTruckId) {
      setBearerInfo(null);
      clearHidden();
      return undefined;
    }
    let cancelled = false;
    inboundRentalsAPI
      .getCostBearer(bearerTruckId, bearerCategory, bearerDate || undefined)
      .then((found) => {
        if (cancelled) return;
        setBearerInfo(found || null);
        if (found?.contract_id) {
          if (!editing && !bearerTouched.current) {
            setForm((current) => ({ ...current, cost_bearer: found.cost_bearer || 'SOCIETE' }));
          }
        } else {
          clearHidden();
        }
      })
      .catch(() => {
        // Sans réponse du serveur, le choix reste masqué (société par défaut).
        if (cancelled) return;
        setBearerInfo(null);
        clearHidden();
      });
    return () => {
      cancelled = true;
    };
  }, [rentalsActive, bearerField, bearerTruckId, bearerCategory, bearerDate, editing]);

  const bearerVisible =
    rentalsActive && Boolean(bearerInfo?.contract_id || (editing && record?.cost_bearer));

  const fields = spec.fields.filter((field) => {
    if (editing && field.createOnly) return false;
    if (!editing && field.editOnly) return false;
    if (field.type === 'bearer' && !bearerVisible) return false;
    return field.when ? field.when(form) : true;
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const found = spec.validate ? spec.validate(form, { editing }) : {};
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    try {
      const payload = editing && spec.toPatch ? spec.toPatch(form) : spec.toPayload(form);
      // Le scan part d'abord: si le dépôt échoue, l'échéance n'est pas créée
      // sans son papier et la saisie reste à l'écran.
      if (spec.upload && form.scan) {
        try {
          Object.assign(payload, await spec.upload(form));
        } catch (uploadError) {
          toast.error(messageErreur(uploadError, 'Le scan n’a pas pu être déposé.'));
          return;
        }
      }
      await onSubmit(payload, editing);
    } catch {
      // Le message d'erreur est déjà porté par le toast de `run`; garder le
      // dialogue ouvert permet de corriger la saisie sans la retaper.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle>{editing ? `Modifier — ${spec.title}` : spec.title}</DialogTitle>
            <Aide titre={spec.title}>{spec.description}</Aide>
          </div>
          <DialogDescription className="sr-only">{spec.description}</DialogDescription>
        </DialogHeader>

        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          {fields.map((field) => {
            const id = `field-${field.name}`;
            const common = { id, 'aria-invalid': Boolean(errors[field.name]) };
            return (
              <div key={field.name} className={field.full ? 'sm:col-span-2' : undefined}>
                {field.type === 'switch' ? (
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
                    <Label htmlFor={id} className="cursor-pointer">
                      {field.label}
                    </Label>
                    <Switch
                      id={id}
                      checked={Boolean(form[field.name])}
                      onCheckedChange={(checked) => set(field.name, checked)}
                    />
                  </div>
                ) : (
                  <Field
                    id={id}
                    label={field.label}
                    hint={
                      field.type === 'bearer' && bearerInfo?.lessor_name
                        ? `Camion loué chez ${bearerInfo.lessor_name} : proposé d’après son contrat, vous pouvez le changer.`
                        : field.hint
                    }
                    error={errors[field.name]}
                  >
                    {field.type === 'truck' && (
                      <SearchableSelect
                        id={id}
                        placeholder="Choisir un camion"
                        searchPlaceholder="Rechercher une immatriculation…"
                        emptyMessage="Aucun camion trouvé"
                        value={form[field.name] || ''}
                        onValueChange={(value) => {
                          // Un autre camion = un autre contrat: le choix « Qui paie ? » repart du contrat.
                          bearerTouched.current = false;
                          set(field.name, value);
                        }}
                        options={truckOptions}
                      />
                    )}
                    {field.type === 'select' && (
                      <Select
                        value={form[field.name]}
                        onValueChange={(value) => set(field.name, value)}
                      >
                        <SelectTrigger id={id}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.type === 'bearer' && (
                      <Select
                        value={form[field.name] || 'SOCIETE'}
                        onValueChange={(value) => {
                          bearerTouched.current = true;
                          set(field.name, value);
                        }}
                      >
                        <SelectTrigger id={id} data-testid="field-cost_bearer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['SOCIETE', 'PROPRIETAIRE'].map((code) => (
                            <SelectItem key={code} value={code}>
                              {libelle('porteur_frais', code)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.type === 'file' && (
                      <Input
                        id={id}
                        type="file"
                        accept="image/*,application/pdf"
                        data-testid={`field-${field.name}-input`}
                        onChange={(event) => set(field.name, event.target.files?.[0] || null)}
                      />
                    )}
                    {field.type === 'textarea' && (
                      <Textarea
                        {...common}
                        value={form[field.name]}
                        placeholder={field.placeholder}
                        onChange={(event) => set(field.name, event.target.value)}
                      />
                    )}
                    {['text', 'number', 'date'].includes(field.type) && (
                      <Input
                        {...common}
                        // `number` reste un champ texte avec un clavier
                        // numérique: le type natif refuse les états
                        // intermédiaires comme « 0, » pendant la frappe.
                        type={field.type === 'date' ? 'date' : 'text'}
                        inputMode={field.type === 'number' ? 'decimal' : undefined}
                        value={form[field.name]}
                        placeholder={field.placeholder}
                        onChange={(event) => set(field.name, event.target.value)}
                      />
                    )}
                  </Field>
                )}
              </div>
            );
          })}

          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Enregistrement…' : spec.submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Levée d'immobilisation                                              */
/* ------------------------------------------------------------------ */

function ReleaseDialog({ record, truckLabel, onClose, onConfirm }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    if (submitting) return;
    if (!note.trim()) {
      setError('Écrivez ce qui autorise la remise en route : la note reste au dossier.');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(note.trim());
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remettre {truckLabel} en service</AlertDialogTitle>
          <AlertDialogDescription>
            Le camion redeviendra affectable immédiatement. Motif de
            l’immobilisation levée : « {record.reason} ».
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Field
          id="release-note"
          label="Ce qui autorise la remise en route"
          error={error}
          hint="Réparation faite, papier renouvelé, dérogation…"
        >
          <Textarea
            id="release-note"
            value={note}
            aria-invalid={Boolean(error)}
            onChange={(event) => {
              setNote(event.target.value);
              if (error) setError('');
            }}
          />
        </Field>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
          <Button onClick={confirm} disabled={submitting}>
            {submitting ? 'Levée…' : 'Lever l’immobilisation'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------------------------------------------ */
/* Clôture d'ordre de travail                                          */
/* ------------------------------------------------------------------ */

function CloseWorkOrderDialog({ record, truckLabel, onClose, onConfirm }) {
  const [form, setForm] = useState({
    labor_cost: record.labor_cost != null ? String(record.labor_cost) : '',
    parts_cost: record.parts_cost != null ? String(record.parts_cost) : '',
    other_cost: record.other_cost != null ? String(record.other_cost) : '',
    odometer_km: record.odometer_km != null ? String(record.odometer_km) : '',
    completed_at: todayIso(),
    closed_note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [variance, setVariance] = useState(null);

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const total =
    (toNumber(form.labor_cost) || 0) +
    (toNumber(form.parts_cost) || 0) +
    (toNumber(form.other_cost) || 0);

  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await onConfirm({
        labor_cost: toNumber(form.labor_cost),
        parts_cost: toNumber(form.parts_cost),
        other_cost: toNumber(form.other_cost),
        odometer_km: toNumber(form.odometer_km),
        completed_at: form.completed_at || null,
        closed_note: form.closed_note || null,
      });
      // L'écart au budget du plan est la seule chose que la clôture apprend
      // et qu'on ne peut plus lire ensuite : il s'affiche avant de fermer.
      if (result?.cost_variance) setVariance(result.cost_variance);
      else onClose();
    } catch {
      setSubmitting(false);
    }
  };

  if (variance) {
    const over = num(variance.delta) > 0;
    return (
      <AlertDialog open onOpenChange={(open) => !open && onClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ordre clôturé</AlertDialogTitle>
            <AlertDialogDescription>
              L’intervention est close et ce qu’elle immobilisait est levé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <dl className="space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Budget prévu au plan</dt>
              <dd className="font-mono tabular">{money(variance.estimated_cost)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Coût réel</dt>
              <dd className="font-mono tabular font-semibold">{money(variance.actual_cost)}</dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-border pt-2">
              <dt className="text-muted-foreground">Écart</dt>
              <dd
                className={`font-mono tabular font-semibold ${
                  over ? 'text-destructive' : 'text-success'
                }`}
              >
                {over ? '+' : ''}
                {money(variance.delta)} ({over ? '+' : ''}
                {num(variance.delta_pct).toFixed(1)} %)
              </dd>
            </div>
          </dl>
          <AlertDialogFooter>
            <Button onClick={onClose}>Fermer</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Clôturer {referenceLisible(record.order_number)} — {truckLabel}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Saisissez ce que l’intervention a réellement coûté. La clôture lève
            l’immobilisation liée et repart le plan préventif depuis le compteur
            indiqué.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="close-labor" label="Main d’œuvre (FCFA)">
            <Input
              id="close-labor"
              inputMode="decimal"
              value={form.labor_cost}
              onChange={(event) => set('labor_cost', event.target.value)}
            />
          </Field>
          <Field id="close-parts" label="Pièces (FCFA)">
            <Input
              id="close-parts"
              inputMode="decimal"
              value={form.parts_cost}
              onChange={(event) => set('parts_cost', event.target.value)}
            />
          </Field>
          <Field id="close-other" label="Autres frais (FCFA)">
            <Input
              id="close-other"
              inputMode="decimal"
              value={form.other_cost}
              onChange={(event) => set('other_cost', event.target.value)}
            />
          </Field>
          <Field
            id="close-odo"
            label="Compteur à la sortie (km)"
            hint="Sert de nouveau point de départ au plan préventif."
          >
            <Input
              id="close-odo"
              inputMode="decimal"
              value={form.odometer_km}
              onChange={(event) => set('odometer_km', event.target.value)}
            />
          </Field>
          <Field id="close-date" label="Terminé le">
            <Input
              id="close-date"
              type="date"
              value={form.completed_at}
              onChange={(event) => set('completed_at', event.target.value)}
            />
          </Field>
          <div className="flex flex-col justify-end">
            <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
              Total : <span className="font-mono tabular font-semibold">{money(total)}</span>
            </p>
          </div>
          <div className="sm:col-span-2">
            <Field id="close-note" label="Ce qui a été fait à l’atelier">
              <Textarea
                id="close-note"
                value={form.closed_note}
                onChange={(event) => set('closed_note', event.target.value)}
              />
            </Field>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
          <Button onClick={confirm} disabled={submitting}>
            {submitting ? 'Clôture…' : 'Clôturer l’ordre'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------------------------------------------ */
/* Pièces d'un ordre de travail                                        */
/* ------------------------------------------------------------------ */

function PartsDialog({ record, canWrite, onClose, onAdded }) {
  const [parts, setParts] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ part_name: '', quantity: '1', unit_cost: '', supplier: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setParts(await maintenanceAPI.getParts(record.id));
    } catch {
      setError(true);
      setParts([]);
    }
  }, [record.id]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!form.part_name.trim()) {
      setFormError('Nommez la pièce montée.');
      return;
    }
    setSubmitting(true);
    try {
      await maintenanceAPI.addPart(record.id, {
        part_name: form.part_name.trim(),
        quantity: toNumber(form.quantity) ?? 1,
        unit_cost: toNumber(form.unit_cost),
        supplier: form.supplier || null,
      });
      setForm({ part_name: '', quantity: '1', unit_cost: '', supplier: '' });
      setFormError('');
      await load();
      onAdded();
      toast.success('Pièce ajoutée');
    } catch {
      toast.error('La pièce n’a pas pu être ajoutée');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle>Pièces — {referenceLisible(record.order_number)}</DialogTitle>
            <Aide titre="Pièces de l’ordre">
              Le coût des pièces remonte automatiquement dans le total de l’ordre.
            </Aide>
          </div>
          <DialogDescription>{record.title}</DialogDescription>
        </DialogHeader>

        {parts === null ? (
          <LoadingState shape="table" rows={3} columns={4} />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Les pièces n’ont pas pu être chargées"
            description="Le total affiché sur l’ordre reste juste ; c’est le détail qui manque."
            actionLabel="Réessayer"
            onAction={load}
          />
        ) : parts.length === 0 ? (
          <EmptyState
            variant="empty"
            title="Aucune pièce enregistrée"
            description="Ajoutez les pièces montées pour que le coût réel de l’intervention soit complet."
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <Table stickyFirstColumn={false}>
              <TableHeader>
                <TableRow>
                  <TableHead>Pièce</TableHead>
                  <TableHead>Quantité</TableHead>
                  <TableHead>Prix unitaire</TableHead>
                  <TableHead>Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parts.map((part) => (
                  <TableRow key={part.id}>
                    <TableCell>
                      <p className="font-medium">{part.part_name}</p>
                      {part.supplier && (
                        <p className="text-xs text-muted-foreground">{part.supplier}</p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono tabular">{num(part.quantity)}</TableCell>
                    <TableCell className="font-mono tabular">{money(part.unit_cost)}</TableCell>
                    <TableCell className="font-mono tabular font-semibold">
                      {money(part.total_cost ?? num(part.quantity) * num(part.unit_cost))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {canWrite && !['done', 'cancelled'].includes(record.status) && (
          <form className="grid gap-3 sm:grid-cols-4" onSubmit={add}>
            <div className="sm:col-span-2">
              <Field id="part-name" label="Pièce" error={formError}>
                <Input
                  id="part-name"
                  value={form.part_name}
                  aria-invalid={Boolean(formError)}
                  onChange={(event) => {
                    setForm({ ...form, part_name: event.target.value });
                    if (formError) setFormError('');
                  }}
                />
              </Field>
            </div>
            <Field id="part-qty" label="Quantité">
              <Input
                id="part-qty"
                inputMode="decimal"
                value={form.quantity}
                onChange={(event) => setForm({ ...form, quantity: event.target.value })}
              />
            </Field>
            <Field id="part-cost" label="Prix unitaire">
              <Input
                id="part-cost"
                inputMode="decimal"
                value={form.unit_cost}
                onChange={(event) => setForm({ ...form, unit_cost: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-4 flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Ajout…' : 'Ajouter la pièce'}
              </Button>
            </div>
          </form>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Petites pièces partagées                                            */
/* ------------------------------------------------------------------ */

/** L'état porte la couleur, jamais la colonne : `Badge` est la seule table. */
function StateBadge({ variant, label }) {
  return <Badge variant={variant}>{label}</Badge>;
}

function TruckLink({ id, label, tab = 'maintenance' }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!id) return <span className="text-muted-foreground">{label}</span>;
  return (
    <button
      type="button"
      onClick={() =>
        navigate(`/trucks/${id}?tab=${tab}`, {
          state: { from: `${location.pathname}${location.search}` },
        })
      }
      className="inline-flex items-center gap-1 rounded font-semibold text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {label}
      <ExternalLink className="h-3 w-3 opacity-50" aria-hidden="true" />
    </button>
  );
}
